// React Dependencies
import React, { useEffect, useState, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { toast } from 'react-toastify';
import Pagination from '@material-ui/lab/Pagination';

// Internal Dependencies
import { IsEmpty } from '../../../common/checks';
import { Status, PageType } from '../../../common/constants';
import { Trim } from '../../../common/util';
import SortableCard from './sortableCard';
import SearchBar from './searchBar';
import {
  addPinnedPost,
  deletePinnedPost,
  getPostsByBrandAndTag,
  getPinnedPosts, getPagePostsExceptHighlights, updatePinnedPost, updatePinnedPostOrder, getPage } from '../../../actions/admin/pageAction';
import SearchCard from './searchCard';
import OverLoading from '../../global/OverLoading';

const PinnedPostList = ({
  match,
  getPage,
  addPinnedPost,
  updatePinnedPost, updatePinnedPostOrder, deletePinnedPost, getPostsByBrandAndTag, getPinnedPosts, getPagePostsExceptHighlights }) => {
  const SortableCardRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [existingPinnedPost, setExistingPinnedPost] = useState([]);
  const [currentTags, setCurrentTags] = useState([]);
  const [brandSearch, setBrandSearch] = useState(null);
  const [pageSize] = useState(20);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [allPostCount, setAllPostCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [openAdd, setOpenAdd] = useState(false);
  const [pinnedPostData, setPinnedPostData] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const pageId = (match && match.params) ? match.params.id : null;
  const [pageInfo, setPageInfo] = useState(null);

  const fetchPinnedPost = async () => {
    const pinData = [];
    const pageData = await getPage(pageId);
    const pageTags = pageData.userTags;

    setPageInfo(pageData);
    setCurrentTags(pageTags);

    const pinnedPostsData = await getPinnedPosts({ pageId });
    const sortedPinnedPosts = _.orderBy(pinnedPostsData, (p) => p.position);
    setExistingPinnedPost(sortedPinnedPosts);

    if (sortedPinnedPosts) {
      sortedPinnedPosts.forEach((v) => {
        const isVideo = !!(!IsEmpty(v.videos) && v.videos.length > 0);
        pinData.push({
          ...v,
          pageId,
          name:        v.brand,
          subtitle:    v.postDate,
          description: IsEmpty(v.description) ? ' ' : v.description,
          isVideo,
          mediaUrl:    isVideo ? v.videos[0] : (v.images[0] ? v.images[0].display : ''),
          logoUrl:     v.logo,
        });
      });
    }

    if (pinData.length > 0) {
      setPinnedPostData(sortedPinnedPosts);
      if (!_.isEmpty(SortableCardRef.current)) {
        SortableCardRef.current.updateItems(sortedPinnedPosts);
      }
    }

    setRefresh(false);
  };

  const updateSeq = async (data) => {
    try {
      setLoading(true);
      const res = await updatePinnedPostOrder(data);
      if (res) {
        toast.success('Position updated');

        fetchPinnedPost();
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onSortableCardEvent = async (eventData) => {
    const newOrdering = eventData.data.new.map((v) => v.id);
    const pinnedPosts = { pageId, pinnedPosts: newOrdering };
    updateSeq(pinnedPosts);
  };

  useEffect(() => {
    if (refresh) {
      fetchPinnedPost();
    }
  }, [refresh]);

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setRefresh(true);
  };

  const onSearch = async () => {
    try {
      const mapExistingOfPinnedPostIds = existingPinnedPost.map((e) => e.postId);
      const brandKey = Trim(brandSearch, false, true);
      let data = null;

      setLoading(true);

      if ([PageType.Mall, PageType.AreaManagement].includes(pageInfo.type)) {
        const res = await getPagePostsExceptHighlights({ pageId, title: brandKey });
        data = {
          result:     res,
          totalCount: res.length,
        };
      } else {
        data = await getPostsByBrandAndTag({
          brand:  brandKey,
          tags:   currentTags,
          pageSize, pageNo:
          pageNo - 1,
          status:           Status.Live,
          searchableStatus: Status.Live,
        });
      }

      setAllPostCount(data.totalCount);
      setTotalCount(Math.ceil(data.totalCount / pageSize));

      const updatedData = _.map(data.result, (d) => _.assign({
        ...d,
        isPinned: mapExistingOfPinnedPostIds.includes(d.id),
      }));
      setPosts(updatedData);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (openAdd) {
      onSearch();
    }
  }, [openAdd, brandSearch, pageNo]);

  const onClickAdd = () => {
    setRefresh(true);
    setBrandSearch(null);
    setPageNo(1);
    setOpenAdd(true);
  };

  const onAddPinnedPost = async (data) => {
    try {
      const filterPosts = posts.filter((p) => p.id !== data.postId);
      const payload = {
        ...data,
        pageId,
      };

      const res = await addPinnedPost(payload);
      if (res) {
        toast.success('Successfully pinned');
        setPosts(filterPosts);
        fetchPinnedPost();
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onDeletePinnedPost = async (postId) => {
    try {
      setLoading(true);
      await deletePinnedPost(pageId, postId);
      const filteredPinPosts = existingPinnedPost.filter((pp) => pp.postId !== postId);
      const newPinnedPosts = { pageId, pinnedPosts: filteredPinPosts.map((fpp) => fpp.postId) };
      updateSeq(newPinnedPosts);
      setLoading(false);
      toast.success('Successfully deleted');
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  const onUpdatePosType = async (data) => {
    try {
      const payload = {
        ...data,
        pageId,
      };
      setLoading(true);
      const res = await updatePinnedPost(payload);
      if (res) {
        toast.success('Position type updated');
        fetchPinnedPost();
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    }
  };

  return (
    <div>
      {loading && <OverLoading />}
      <>
        <div className="content-header" style={{ backgroundColor: '#fff' }}>
          <h1>Pinned Posts</h1>
          <Button onClick={onClickAdd} style={{ marginLeft: 'auto' }} color="primary" variant="contained" size="large" disabled={IsEmpty(currentTags)}>Add</Button>
        </div>
        {
          !IsEmpty(currentTags) ? (pinnedPostData.length === 0
            ? <div className="d-block mx-auto mt-5"><h4 className="text-center">No pinned post, add some.</h4></div>
            : (
              <div>
                <h5>*Click-and-hold item for 2sec to select and move it</h5>
                <SortableCard ref={SortableCardRef} onEvent={onSortableCardEvent} onDelete={(postId) => onDeletePinnedPost(postId)} onUpdatePosType={(data) => onUpdatePosType(data)} cards={existingPinnedPost} />
              </div>
            )) : <div className="d-block mx-auto mt-5"><h4 className="text-center">No tag(s) was found on this page</h4></div>
        }
      </>
      <Dialog fullWidth maxWidth="lg" maxHeight="lg" open={openAdd} onClose={handleCloseAdd} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title" style={{ paddingTop: 20, paddingLeft: 50, paddingRight: 50 }}>
          <SearchBar
            onSearch={(brand) => {
              setPageNo(1);
              setBrandSearch(brand);
            }}
            existingPinnedPost={existingPinnedPost}
            label="Search by Brand"
            style={{ width: '100%' }}
          />
        </DialogTitle>
        <DialogContent style={{ height: '80vh', paddingLeft: 50, paddingRight: 50 }}>
          {!loading ? (
            <div className="container">
              <div className="row">
                <SearchCard data={posts} lastIndex={existingPinnedPost.length} onAdd={(data) => onAddPinnedPost(data)} />
              </div>
            </div>
          ) : <OverLoading />}
        </DialogContent>
        <DialogActions style={{ paddingTop: 20, paddingRight: 20, paddingBottom: 10 }}>
          <div className="d-block" style={{ width: '100%' }}>
            <div className="float-left">
              {totalCount > 0 && (
                <Pagination
                  color="secondary"
                  size="large"
                  count={totalCount}
                  page={pageNo}
                  onChange={(event, value) => {
                    setPageNo(value);
                  }}
                  className="float-left"
                />
              )}
              <p style={{
                display:  'inline-block',
                margin:   '10px 0 0 30px',
                fontSize: 17,
              }}
              >
                Total count:
                {' '}
                {allPostCount}
              </p>
            </div>

            <Button onClick={handleCloseAdd} variant="contained" color="primary" className="float-right">Close</Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

PinnedPostList.propTypes = {
  match:                        PropTypes.shape(Object).isRequired,
  getPage:                      PropTypes.func.isRequired,
  addPinnedPost:                PropTypes.func.isRequired,
  updatePinnedPost:             PropTypes.func.isRequired,
  updatePinnedPostOrder:        PropTypes.func.isRequired,
  deletePinnedPost:             PropTypes.func.isRequired,
  getPostsByBrandAndTag:        PropTypes.func.isRequired,
  getPinnedPosts:               PropTypes.func.isRequired,
  getPagePostsExceptHighlights: PropTypes.func.isRequired,
};

export default connect(null, {
  getPage,
  addPinnedPost,
  deletePinnedPost,
  getPostsByBrandAndTag,
  getPinnedPosts,
  getPagePostsExceptHighlights,
  updatePinnedPost,
  updatePinnedPostOrder,
})(PinnedPostList);
